import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RefSelect = ({ label, value, onChange, options, placeholder, testId }) => (
  <div className="space-y-1">
    <Label>{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="none">— None —</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

export const AddTaskDialog = ({ open, onOpenChange, refs, onCreated }) => {
  const empty = { name: '', description: '', project_id: 'none', assigned_to: 'none', priority: 'medium', status: 'todo', due_date: '' };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(empty); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/tasks`, {
        name: form.name,
        description: form.description || null,
        project_id: form.project_id === 'none' ? null : form.project_id,
        assigned_to: form.assigned_to === 'none' ? null : form.assigned_to,
        priority: form.priority,
        status: form.status,
        due_date: form.due_date || null,
      });
      toast.success(form.assigned_to !== 'none' ? 'Task created — assignee will be notified by email' : 'Task created');
      onOpenChange(false);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create task');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Add Task</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1">
            <Label>Task Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="add-task-name" />
          </div>
          <div className="space-y-1">
            <Label>Description</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <RefSelect label="Link to Project" value={form.project_id} onChange={(v) => setForm({ ...form, project_id: v })}
            options={refs.projects.map((p) => ({ id: p.id, label: p.name }))} placeholder="Select existing project" testId="add-task-project" />
          <RefSelect label="Assign To" value={form.assigned_to} onChange={(v) => setForm({ ...form, assigned_to: v })}
            options={refs.members.map((m) => ({ id: m.id, label: m.name }))} placeholder="Select team member" testId="add-task-assignee" />
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['low', 'medium', 'high', 'urgent'].map((p) => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['todo', 'in_progress', 'awaiting_feedback', 'done'].map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Due Date</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="add-task-submit">{saving ? 'Saving...' : 'Create Task'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const AddProjectDialog = ({ open, onOpenChange, refs, onCreated }) => {
  const empty = { name: '', description: '', client_id: 'none', manager_id: 'none', status: 'planning', priority: 'medium', start_date: '', end_date: '', budget: '' };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(empty); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const client = refs.clients.find((c) => c.id === form.client_id);
      await axios.post(`${API}/projects`, {
        name: form.name,
        description: form.description || null,
        client_id: form.client_id === 'none' ? null : form.client_id,
        client_name: client ? client.company : null,
        manager_id: form.manager_id === 'none' ? null : form.manager_id,
        status: form.status,
        priority: form.priority,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        budget: parseFloat(form.budget) || 0,
      });
      toast.success('Project created — the team will be notified by email');
      onOpenChange(false);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create project');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Add Project</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1">
            <Label>Project Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="add-project-name" />
          </div>
          <div className="space-y-1">
            <Label>Description</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <RefSelect label="Link to Client" value={form.client_id} onChange={(v) => setForm({ ...form, client_id: v })}
            options={refs.clients.map((c) => ({ id: c.id, label: c.company }))} placeholder="Select existing client" testId="add-project-client" />
          <RefSelect label="Project Manager" value={form.manager_id} onChange={(v) => setForm({ ...form, manager_id: v })}
            options={refs.members.map((m) => ({ id: m.id, label: m.name }))} placeholder="Select team member" testId="add-project-manager" />
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['planning', 'active', 'on_hold', 'completed', 'cancelled'].map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['low', 'medium', 'high', 'urgent'].map((p) => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Budget (R)</Label>
              <Input type="number" min="0" step="0.01" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Start Date</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>End Date</Label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="add-project-submit">{saving ? 'Saving...' : 'Create Project'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const AddLeadDialog = ({ open, onOpenChange, refs, onCreated }) => {
  const empty = { name: '', company: '', email: '', phone: '', status: 'new', assigned_to: 'none', notes: '' };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(empty); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        company: form.company || null,
        email: form.email || null,
        phone: form.phone || null,
        status: form.status,
        source: 'manual',
        assigned_to: form.assigned_to === 'none' ? null : form.assigned_to,
        notes: form.notes || null,
      });
      toast.success('Lead created');
      onOpenChange(false);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create lead');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Add Lead</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Lead Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="add-lead-name" />
            </div>
            <div className="space-y-1">
              <Label>Company</Label>
              <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['new', 'contacted', 'qualified', 'unqualified'].map((s) => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <RefSelect label="Assign To" value={form.assigned_to} onChange={(v) => setForm({ ...form, assigned_to: v })}
              options={refs.members.map((m) => ({ id: m.id, label: m.name }))} placeholder="Select team member" testId="add-lead-assignee" />
          </div>
          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="add-lead-submit">{saving ? 'Saving...' : 'Create Lead'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
